import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  MapPin,
  LogOut,
  Moon,
  Sun,
  Edit,
  Languages,
  Store,
  Settings,
  Crown,
  Copy,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import apis from "../../utils/apis";
import { useTheme } from "../../context/ThemeContext";
import Modal from "../../components/Modal";
import toast from "react-hot-toast";
import { clearLocalStorageExcept } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import { resetReduxStore, clearUserStorage } from "../../utils/reduxUtils";
import { updateUserInfo } from "../../store/slices/userSlice";
import { formatPrice } from "../../utils/utils";

const ProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state?.user?.userInfo);
  const activeMembership = user?.activePremiumMembership;
  const { isDarkMode, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [newName, setNewName] = useState(user?.name || "");
  const { callApi: updateUserName, isLoading: updateNameLoading } = useApi();
  const { callApi: fetchMembershipStats } = useApi();
  const [membershipStats, setMembershipStats] = useState(null);
  const [language, setLanguage] = useState(i18n.language || "en");

  useEffect(() => {
    if (!activeMembership) {
      setMembershipStats(null);
      return;
    }
    fetchMembershipStats({ api: apis.membershipStats })
      .then((data) => setMembershipStats(data))
      .catch(() => setMembershipStats(null));
  }, [activeMembership?.code]);

  const copyMembershipCode = async () => {
    if (!activeMembership?.code) return;
    try {
      await navigator.clipboard.writeText(activeMembership.code);
      toast.success("Membership code copied");
    } catch {
      toast.error("Could not copy code");
    }
  };
  const handleLogout = () => {
    setIsLogoutModalOpen(false);

    // Get current user ID before clearing
    const userId = user?.id || user?._id || "guest";

    // Clear user-specific Redux state storage
    clearUserStorage(userId);

    // Reset all Redux slices to initial state
    resetReduxStore(dispatch);

    // Clear all TanStack Query cache to prevent data leakage between accounts
    queryClient.clear();

    // Clear other localStorage except firstLogin and language
    clearLocalStorageExcept({ values: ["firstLogin", "language"] });

    navigate("/login");
  };

  const confirmLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const handleUpdateName = async () => {
    try {
      await updateUserName({
        api: apis.updateUserFromUser,
        body: { name: newName },
      });
      dispatch(updateUserInfo({ name: newName }));
      setIsNameModalOpen(false);
      toast.success(t("profile.nameUpdateSuccess"));
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error(t("profile.nameUpdateError"));
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
    // Use setTimeout to ensure translation is loaded before showing toast
    setTimeout(() => {
      toast.success(t("profile.languageChanged"));
    }, 100);
  };

  return (
    <div className="bg-light-background dark:bg-dark-background font-sans page">
      <header className="bg-white dark:bg-dark-bg shadow-sm sticky top-0 z-10 section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <Link
            to="/"
            className="text-light-foreground dark:text-dark-foreground hover:text-primary-100 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-2xl font-bold ml-4 text-light-foreground dark:text-dark-foreground">
            {t("profile.title")}
          </h1>
        </div>
      </header>

      <main className="p-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-dark-bg rounded-lg shadow-md overflow-hidden section"
        >
          <div className="p-6 border-b border-light-border dark:border-border-100">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-semibold text-light-foreground dark:text-dark-foreground capitalize">
                  {user?.name || t("profile.setYourName")}
                </h2>
                <p className="text-light-gray-600 dark:text-dark-gray-400">
                  +91 {user?.mobile}
                </p>
              </div>
              <button
                onClick={() => setIsNameModalOpen(true)}
                className="text-primary-100 hover:text-primary-600 transition-colors"
              >
                <Edit className="w-6 h-6" />
              </button>
            </div>
          </div>

          {activeMembership ? (
            <div className="p-6 border-b border-light-border dark:border-border-100 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-500 rounded-full">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                    Premium Member{activeMembership.planName ? ` · ${activeMembership.planName}` : ""}
                  </h3>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                    {activeMembership.discountPercent}% off on orders
                    {activeMembership.maxDiscountAmount
                      ? ` (up to ₹${formatPrice(activeMembership.maxDiscountAmount)})`
                      : ""}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                    Valid until {new Date(activeMembership.validUntil).toLocaleDateString("en-IN")}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <code className="text-sm font-mono bg-white/80 dark:bg-dark-bg px-2 py-1 rounded">
                      {activeMembership.code}
                    </code>
                    <button
                      type="button"
                      onClick={copyMembershipCode}
                      className="text-amber-700 dark:text-amber-300"
                      aria-label="Copy membership code"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  {membershipStats?.totalSavings > 0 && (
                    <p className="text-sm font-medium text-green-700 dark:text-green-400 mt-3">
                      You&apos;ve saved ₹{formatPrice(membershipStats.totalSavings?.toFixed(2))} as a premium member
                    </p>
                  )}
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    Applied automatically at checkout
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-6 py-4 border-b border-light-border dark:border-border-100">
              <p className="text-sm text-light-gray-600 dark:text-dark-gray-400">
                No active premium membership
              </p>
            </div>
          )}

          <nav>
            <Link
              to="/orders"
              className="flex items-center justify-between p-4 hover:bg-light-gray-100 dark:hover:bg-dark-gray-700 transition-colors"
            >
              <div className="flex items-center">
                <ShoppingBag className="w-6 h-6 text-primary-100 mr-3" />
                <span className="text-light-foreground dark:text-dark-foreground">
                  {t("profile.orders")}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-light-gray-400 dark:text-dark-gray-400" />
            </Link>
            <Link
              to="/addresses"
              className="flex items-center justify-between p-4 hover:bg-light-gray-100 dark:hover:bg-dark-gray-700 transition-colors"
            >
              <div className="flex items-center">
                <MapPin className="w-6 h-6 text-primary-100 mr-3" />
                <span className="text-light-foreground dark:text-dark-foreground">
                  {t("profile.addresses")}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-light-gray-400 dark:text-dark-gray-400" />
            </Link>

            {/* Language Switcher */}
            <div className="flex items-center justify-between p-4 hover:bg-light-gray-100 dark:hover:bg-dark-gray-700 transition-colors cursor-pointer">
              <div className="flex items-center">
                <Languages className="w-6 h-6 text-primary-100 mr-3" />
                <span className="text-light-foreground dark:text-dark-foreground">
                  {t("profile.language")}
                </span>
              </div>
              <select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="bg-transparent text-light-foreground dark:text-dark-foreground border-none outline-none section"
              >
                <option value="en">English</option>
                <option value="hi">हिंदी</option>
              </select>
            </div>

            {user?.defaultAddress && (
              <div className="p-4 bg-light-gray-50 dark:bg-dark-gray-800">
                <h3 className="text-sm font-medium text-light-gray-500 dark:text-dark-gray-400 mb-1">
                  {t("profile.defaultAddress")}:
                </h3>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-primary-100 mr-2" />
                  <p className="text-light-foreground dark:text-dark-foreground">
                    {`${user.defaultAddress.address ?? ""}, ${
                      user.defaultAddress.city
                    } - ${user.defaultAddress.pin_code}`}
                  </p>
                </div>
              </div>
            )}

            {/* Selected Mart Tile */}
            {user?.defaultAddress?.nearby && (
              <div className="p-4 bg-light-gray-50 dark:bg-dark-gray-800 border-t border-light-border dark:border-border-100">
                <h3 className="text-sm font-medium text-light-gray-500 dark:text-dark-gray-400 mb-1">
                  {t("profile.martAddress")}:
                </h3>
                <div className="flex items-center">
                  <Store className="w-4 h-4 text-primary-100 mr-2" />
                  <div>
                    <p className="text-light-foreground dark:text-dark-foreground font-medium">
                      {user.defaultAddress.nearby.mart?.name ?? ""}
                    </p>
                    <p className="text-light-foreground dark:text-dark-foreground text-sm">
                      {user.defaultAddress.nearby.mart?.martId ?? ""}
                    </p>
                    <p className="text-xs text-light-gray-600 dark:text-dark-gray-400 mt-1">
                      {user.defaultAddress.nearby.nearbyLocation ?? ""},{" "}
                      {user.defaultAddress.nearby.city ?? ""},{" "}
                      {user.defaultAddress.nearby.state ?? ""} -{" "}
                      {user.defaultAddress.nearby.pinCode ?? ""}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={toggleTheme}
              className="flex items-center justify-between w-full p-4 hover:bg-light-gray-100 dark:hover:bg-dark-gray-700 transition-colors text-left"
            >
              <div className="flex items-center">
                {isDarkMode ? (
                  <Sun className="w-6 h-6 text-primary-100 mr-3" />
                ) : (
                  <Moon className="w-6 h-6 text-primary-100 mr-3" />
                )}
                <span className="text-light-foreground dark:text-dark-foreground">
                  {isDarkMode ? t("profile.lightMode") : t("profile.darkMode")}
                </span>
              </div>
            </button>
            <button
              onClick={confirmLogout}
              className="flex items-center justify-between w-full p-4 hover:bg-light-gray-100 dark:hover:bg-dark-gray-700 transition-colors text-left"
            >
              <div className="flex items-center">
                <LogOut className="w-6 h-6 text-red-600 dark:text-dark-error mr-3" />
                <span className="text-red-600 dark:text-dark-error">
                  {t("profile.logout")}
                </span>
              </div>
            </button>

            {/* Advanced */}
            <Link
              to="/profile/advanced"
              className="flex items-center justify-between p-4 hover:bg-light-gray-100 dark:hover:bg-dark-gray-700 transition-colors border-t border-light-border dark:border-border-100"
            >
              <div className="flex items-center">
                <Settings className="w-6 h-6 text-gray-700 dark:text-gray-400 mr-3" />
                <span className="text-gray-800 dark:text-gray-400">
                  {t("profile.advanced.title")}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </Link>
          </nav>
        </motion.div>
      </main>

      <Modal
        title={t("profile.updateName")}
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
      >
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t("profile.enterYourName")}
          className="w-full p-2 outline-none border border-light-border dark:border-dark-border rounded-md  focus:border-primary-100 bg-light-background dark:bg-dark-background text-light-foreground dark:text-dark-foreground"
        />
        <button
          disabled={!newName?.trim() || updateNameLoading}
          onClick={handleUpdateName}
          className="mt-4 w-full bg-primary-100 text-white py-2 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50"
        >
          {updateNameLoading ? t("profile.updating") : t("profile.updateName")}
        </button>
      </Modal>

      {/* Logout Confirmation Modal (Center) */}
      <Modal
        title={t("profile.logoutModal.title")}
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        position="center"
      >
        <p className="text-light-foreground dark:text-dark-foreground mb-6">
          {t("profile.logoutModal.message")}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setIsLogoutModalOpen(false)}
            className="flex-1 border-2 border-light-gray-300 dark:border-dark-gray-600 text-light-foreground dark:text-dark-foreground py-2 rounded-md hover:bg-light-gray-100 dark:hover:bg-dark-gray-800 transition-colors"
          >
            {t("profile.logoutModal.cancel")}
          </button>
          <button
            onClick={handleLogout}
            className="flex-1 bg-red-600 text-white py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            {t("profile.logoutModal.confirm")}
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default ProfilePage;
